import { useState, useEffect } from "react";
import { Package, Plus, Edit, Trash2, ShoppingCart, Gift, ArrowUpCircle, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";

const InventoryPage = () => {
  const { token, isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [form, setForm] = useState({ name: "", quantity: "", price: "", cost_price: "", category: "", min_stock: "" });
  const [sellForm, setSellForm] = useState({ type: "sell", quantity: 1, discount_type: "", discount_value: "", patient_name: "", notes: "" });

  const fetchItems = async () => {
    try {
      const res = await api.get("/inventory", token);
      setItems(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); }, [token]);

  const handleSubmit = async () => {
    if (!form.name) { toast.error("أدخل اسم المادة"); return; }
    try {
      const data = {
        name: form.name,
        quantity: parseInt(form.quantity) || 0,
        price: parseFloat(form.price) || 0,
        cost_price: form.cost_price ? parseFloat(form.cost_price) : null,
        category: form.category || null,
        min_stock: parseInt(form.min_stock) || 0
      };
      if (editing) {
        await api.put(`/inventory/${editing.id}`, data, token);
        toast.success("تم تعديل المادة");
      } else {
        await api.post("/inventory", data, token);
        toast.success("تم إضافة المادة");
      }
      setShowModal(false);
      setEditing(null);
      setForm({ name: "", quantity: "", price: "", cost_price: "", category: "", min_stock: "" });
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || "خطأ"); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`حذف "${item.name}" من المخزون؟`)) return;
    try {
      await api.delete(`/inventory/${item.id}`, token);
      toast.success("تم الحذف");
      fetchItems();
    } catch (err) { toast.error("خطأ في الحذف"); }
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ name: item.name, quantity: item.quantity?.toString() || "", price: item.price?.toString() || "", cost_price: item.cost_price?.toString() || "", category: item.category || "", min_stock: item.min_stock?.toString() || "" });
    setShowModal(true);
  };

  const openSell = (item, type) => {
    setSelectedItem(item);
    setSellForm({ type, quantity: 1, discount_type: "", discount_value: "", patient_name: "", notes: "" });
    setShowSellModal(true);
  };

  const handleSellSubmit = async () => {
    if (!selectedItem) return;
    try {
      await api.post("/inventory/transaction", {
        item_id: selectedItem.id,
        type: sellForm.type,
        quantity: parseInt(sellForm.quantity) || 1,
        discount_type: sellForm.discount_type || null,
        discount_value: sellForm.discount_value ? parseFloat(sellForm.discount_value) : null,
        patient_name: sellForm.patient_name || null,
        notes: sellForm.notes || null
      }, token);
      toast.success(sellForm.type === "sell" ? "تم البيع" : sellForm.type === "free" ? "تم الصرف المجاني" : "تم إضافة المخزون");
      setShowSellModal(false);
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || "خطأ"); }
  };

  const filtered = items.filter(i => i.name?.includes(search));

  // Calculate sell total
  const calcTotal = () => {
    if (!selectedItem) return 0;
    const unit = selectedItem.price || 0;
    let total = unit * (parseInt(sellForm.quantity) || 1);
    if (sellForm.discount_type === "percent" && sellForm.discount_value) {
      total = total - (total * parseFloat(sellForm.discount_value) / 100);
    } else if (sellForm.discount_type === "fixed" && sellForm.discount_value) {
      total = total - parseFloat(sellForm.discount_value);
    }
    return Math.max(0, total);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse-soft">جاري التحميل...</div></div>;

  return (
    <div className="animate-fade-in" data-testid="inventory-page">
      <div className="flex justify-between items-start flex-wrap gap-3 mb-6">
        <div>
          <h1 className="page-title">المخزون</h1>
          <p className="page-subtitle">إدارة المواد والمنتجات</p>
        </div>
        <button onClick={() => { setEditing(null); setForm({ name: "", quantity: "", price: "", cost_price: "", category: "", min_stock: "" }); setShowModal(true); }} className="btn btn-primary" data-testid="add-item-btn">
          <Plus className="w-4 h-4" /> إضافة مادة
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="w-5 h-5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
        <input type="text" className="form-input pr-10" placeholder="بحث بالاسم..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-input" />
      </div>

      {/* Low stock alerts */}
      {items.filter(i => i.min_stock > 0 && i.quantity <= i.min_stock).length > 0 && (
        <div className="card p-3 mb-4 bg-red-50 border-red-200">
          <div className="flex items-center gap-2 text-red-700 text-sm font-bold">
            <AlertTriangle className="w-4 h-4" /> مواد وصلت لحد التنبيه:
            {items.filter(i => i.min_stock > 0 && i.quantity <= i.min_stock).map(i => (
              <span key={i.id} className="px-2 py-0.5 bg-red-100 rounded-full">{i.name} ({i.quantity})</span>
            ))}
          </div>
        </div>
      )}

      {/* Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(item => (
          <div key={item.id} className="card p-4 hover:shadow-lg transition-shadow" data-testid={`item-${item.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-800">{item.name}</h3>
                  {item.category && <span className="text-xs text-gray-500">{item.category}</span>}
                </div>
              </div>
              {isAdmin() && (
                <div className="flex gap-1">
                  <button onClick={() => openEdit(item)} data-testid={`edit-item-${item.id}`} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(item)} data-testid={`delete-item-${item.id}`} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-3 gap-2 text-center mb-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <div className={`text-lg font-bold ${item.quantity <= (item.min_stock || 0) && item.min_stock > 0 ? 'text-red-600' : 'text-blue-700'}`}>{item.quantity}</div>
                <div className="text-[10px] text-gray-500">متوفر</div>
              </div>
              <div className="p-2 bg-emerald-50 rounded-lg">
                <div className="text-lg font-bold text-emerald-600">{item.sold || 0}</div>
                <div className="text-[10px] text-gray-500">مباع</div>
              </div>
              <div className="p-2 bg-purple-50 rounded-lg">
                <div className="text-lg font-bold text-purple-600">{item.given_free || 0}</div>
                <div className="text-[10px] text-gray-500">مجاني</div>
              </div>
            </div>

            {item.price > 0 && (
              <div className="text-sm text-gray-600 mb-3">السعر: <strong>{item.price.toLocaleString()}</strong></div>
            )}

            <div className="flex gap-2">
              <button onClick={() => openSell(item, "sell")} data-testid={`sell-item-${item.id}`} className="flex-1 btn btn-sm bg-emerald-500 text-white hover:bg-emerald-600 flex items-center justify-center gap-1">
                <ShoppingCart className="w-3.5 h-3.5" /> بيع
              </button>
              <button onClick={() => openSell(item, "free")} data-testid={`free-item-${item.id}`} className="flex-1 btn btn-sm bg-purple-500 text-white hover:bg-purple-600 flex items-center justify-center gap-1">
                <Gift className="w-3.5 h-3.5" /> مجاني
              </button>
              <button onClick={() => openSell(item, "restock")} data-testid={`restock-item-${item.id}`} className="btn btn-sm bg-blue-500 text-white hover:bg-blue-600 flex items-center justify-center gap-1">
                <ArrowUpCircle className="w-3.5 h-3.5" /> +
              </button>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="empty-state card p-8">
          <Package className="empty-state-icon" />
          <h3>لا توجد مواد</h3>
          <p>أضف مواد جديدة لبدء إدارة المخزون</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>{editing ? "تعديل مادة" : "إضافة مادة جديدة"}</h2><button onClick={() => setShowModal(false)}>✕</button></div>
            <div className="modal-body space-y-4" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              <div className="form-group"><label className="form-label">اسم المادة *</label><input className="form-input" data-testid="item-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="مثال: شامبو" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="form-group"><label className="form-label">الكمية</label><input type="number" className="form-input" data-testid="item-quantity-input" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="0" /></div>
                <div className="form-group"><label className="form-label">سعر البيع</label><input type="number" className="form-input" data-testid="item-price-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="0" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="form-group"><label className="form-label">سعر الشراء (اختياري)</label><input type="number" className="form-input" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} placeholder="0" /></div>
                <div className="form-group"><label className="form-label">حد التنبيه</label><input type="number" className="form-input" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} placeholder="0" /></div>
              </div>
              <div className="form-group"><label className="form-label">الفئة (اختياري)</label><input className="form-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="مثال: منتجات شعر" /></div>
            </div>
            <div className="p-4 border-t bg-gray-50"><button onClick={handleSubmit} data-testid="save-item-btn" className="btn btn-primary w-full">{editing ? "حفظ التعديلات" : "إضافة المادة"}</button></div>
          </div>
        </div>
      )}

      {/* Sell/Free/Restock Modal */}
      {showSellModal && selectedItem && (
        <div className="modal-overlay" onClick={() => setShowSellModal(false)}>
          <div className="modal-content max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{sellForm.type === "sell" ? `بيع ${selectedItem.name}` : sellForm.type === "free" ? `صرف مجاني - ${selectedItem.name}` : `إضافة مخزون - ${selectedItem.name}`}</h2>
              <button onClick={() => setShowSellModal(false)}>✕</button>
            </div>
            <div className="modal-body space-y-4" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <span className="text-sm text-gray-500">المتوفر حالياً:</span>
                <span className="text-xl font-bold text-blue-700 mr-2">{selectedItem.quantity}</span>
              </div>

              <div className="form-group">
                <label className="form-label">الكمية</label>
                <input type="number" className="form-input" min="1" max={sellForm.type === "restock" ? 99999 : selectedItem.quantity} data-testid="txn-quantity-input" value={sellForm.quantity}
                  onChange={(e) => setSellForm({ ...sellForm, quantity: e.target.value })} />
              </div>

              {sellForm.type === "sell" && (
                <>
                  <div className="form-group">
                    <label className="form-label">نوع الخصم (اختياري)</label>
                    <select data-testid="discount-type-select" className="form-input" value={sellForm.discount_type} onChange={(e) => setSellForm({ ...sellForm, discount_type: e.target.value, discount_value: "" })}>
                      <option value="">بدون خصم</option>
                      <option value="percent">نسبة مئوية %</option>
                      <option value="fixed">مبلغ ثابت</option>
                    </select>
                  </div>
                  {sellForm.discount_type && (
                    <div className="form-group">
                      <label className="form-label">{sellForm.discount_type === "percent" ? "نسبة الخصم (%)" : "مبلغ الخصم"}</label>
                      <input type="number" data-testid="discount-value-input" className="form-input" value={sellForm.discount_value}
                        onChange={(e) => setSellForm({ ...sellForm, discount_value: e.target.value })}
                        placeholder={sellForm.discount_type === "percent" ? "مثال: 15" : "مثال: 5000"} />
                    </div>
                  )}
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <div className="flex justify-between text-sm">
                      <span>السعر: {selectedItem.price?.toLocaleString()} × {sellForm.quantity}</span>
                      <span className="font-bold text-emerald-700">{calcTotal().toLocaleString()}</span>
                    </div>
                  </div>
                </>
              )}

              {sellForm.type !== "restock" && (
                <div className="form-group">
                  <label className="form-label">اسم المريض (اختياري)</label>
                  <input className="form-input" value={sellForm.patient_name} onChange={(e) => setSellForm({ ...sellForm, patient_name: e.target.value })} placeholder="اسم المريض" />
                </div>
              )}

              <div className="form-group">
                <label className="form-label">ملاحظات</label>
                <input className="form-input" value={sellForm.notes} onChange={(e) => setSellForm({ ...sellForm, notes: e.target.value })} placeholder="ملاحظات..." />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleSellSubmit} data-testid="confirm-txn-btn" className={`btn w-full ${sellForm.type === "sell" ? "bg-emerald-500 hover:bg-emerald-600" : sellForm.type === "free" ? "bg-purple-500 hover:bg-purple-600" : "bg-blue-500 hover:bg-blue-600"} text-white`}>
                {sellForm.type === "sell" ? `بيع بمبلغ ${calcTotal().toLocaleString()}` : sellForm.type === "free" ? "صرف مجاني" : "إضافة للمخزون"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryPage;
